import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { tap, catchError, of, map } from 'rxjs';

import { isGreaterThanZero, toBig } from '@core';

import { PaymentGatewaysService } from './payment-gateways.service';

import {
  PaymentGateway,
  PaymentMethodResponse,
} from '../../models/payment-gateway.model';

import { mapPaymentGateways } from './payment-gateways.mapper';
import PaymentMethodType from '../../models/payment-method.enum';

@Injectable()
export class PaymentGatewaysState {
  private readonly _service = inject(PaymentGatewaysService);

  readonly filterBy = signal<PaymentMethodType[] | undefined>(undefined);
  readonly selectedId = signal<string | null>(null);

  readonly resource = rxResource({
    params: () => this.filterBy(),
    stream: (lp) => {
      const filterBy = lp.params;

      if (!filterBy?.length) {
        return of({
          providers: [] as PaymentGateway[],
          faultMessage: null as string | null,
          paymentMethodResponses: [] as PaymentMethodResponse[],
        });
      }

      return this._service.getPaymentGateways(filterBy).pipe(
        map((response) => ({
          providers: mapPaymentGateways(response),
          faultMessage: response.faultMessage,
          paymentMethodResponses: response.paymentMethodResponses,
        })),

        tap(({ providers }) => {
          const defaultProvider =
            providers.length === 1
              ? providers[0]
              : (providers.find((p) => p.isDefault) ?? providers[0]);

          this.selectedId.set(defaultProvider?.id ?? null);
        }),

        catchError(() =>
          of({
            providers: [] as PaymentGateway[],
            faultMessage: 'درگاه پرداخت در دسترس نیست لطفا بعدا تلاش فرمایید',
            paymentMethodResponses: [] as PaymentMethodResponse[],
          }),
        ),
      );
    },
  });

  // --------------------------------------------------
  // base collections
  // --------------------------------------------------

  readonly providers = computed<PaymentGateway[]>(
    () => this.resource.value()?.providers ?? [],
  );

  readonly onlineGateways = computed(() =>
    this.providers().filter(
      (gateway) =>
        gateway.paymentMethodType === PaymentMethodType.OnlineGateway,
    ),
  );

  readonly walletProviders = computed(() =>
    this.providers().filter(
      (gateway) => gateway.paymentMethodType === PaymentMethodType.Wallet,
    ),
  );

  // --------------------------------------------------
  // selected
  // --------------------------------------------------

  readonly selectedGateway = computed(() => {
    const id = this.selectedId();

    return this.providers().find((g) => g.id === id) ?? null;
  });

  readonly selectedOnlineGateway = computed(() => {
    const selected = this.selectedGateway();

    return selected?.paymentMethodType === PaymentMethodType.OnlineGateway
      ? selected
      : null;
  });

  readonly selectedWalletProvider = computed(() => {
    const selected = this.selectedGateway();

    return selected?.paymentMethodType === PaymentMethodType.Wallet
      ? selected
      : null;
  });

  // --------------------------------------------------
  // ui state
  // --------------------------------------------------

  readonly loading = computed(() => this.resource.isLoading());
  readonly error = computed(() => this.resource.error());

  readonly hasGateways = computed(() => this.onlineGateways().length > 0);

  readonly hasWalletProviders = computed(
    () => this.walletProviders().length > 0,
  );

  readonly hasPurchaseCapability = computed(() => this.providers().length > 0);

  readonly faultMessage = computed(
    () => this.resource.value()?.faultMessage ?? '',
  );

  readonly paymentMethodResponses = computed(
    () => this.resource.value()?.paymentMethodResponses ?? [],
  );

  // --------------------------------------------------
  // business rules
  // --------------------------------------------------

  isSelectedGatewayEligibleForAmount(amount: string | null): boolean {
    const selected = this.selectedGateway();

    if (!selected) return false;

    return this.isGatewayEligibleForAmount(
      selected.minimum,
      selected.maximum,
      amount,
    );
  }

  isGatewayEligibleForAmount(
    minimum: string | null,
    maximum: string | null,
    amount: string | null,
  ): boolean {
    if (amount === null || !isGreaterThanZero(amount)) {
      return true;
    }

    try {
      const value = toBig(amount);

      if (minimum && value.lt(toBig(minimum))) {
        return false;
      }

      if (maximum && value.gt(toBig(maximum))) {
        return false;
      }
    } catch {
      return false;
    }

    return true;
  }

  // --------------------------------------------------
  // actions
  // --------------------------------------------------

  select(id: string | null): void {
    this.selectedId.set(id);
  }

  reloadPaymentGateways(): void {
    this.resource.reload();
  }
}
